/**
 * The run definition editor (#169 slice 3): a form and a YAML view of one
 * document. The YAML text IS the document and is what Save commits:
 *
 * - The form edits a parsed copy; each form change goes through the
 *   backend's render, which replaces the text (and drops its comments).
 *   While render refuses (an incomplete form, e.g. no pattern yet) the text
 *   stays the last complete definition, its reason is the finding, and the
 *   YAML view and Save wait.
 * - Switching from YAML to the form previews the text first; when it is no
 *   valid definition the form stays disabled and the user stays in YAML.
 * - Preview's findings show live (500 ms after the typing stops). Save is
 *   disabled while any is an error, until the current text's check has
 *   landed, and while the text is the one Save would replace.
 *
 * Save sends the text the editor started from (`base_content`), and after
 * a save the saved text, so the next save pushes onto it. The editor works
 * in LF (a textarea has no CR) and Save restores a CRLF file's endings. A
 * new definition takes its name from the form (or the YAML's `name:`
 * line); once saved its name is fixed like an existing one's.
 */

import { useEffect, useRef, useState, type ReactElement } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { DefinitionEditorHeader } from './DefinitionEditorHeader';
import { DefinitionForm } from './DefinitionForm';
import { EditorFindings, SaveOutcome } from './DefinitionEditorStatus';
import { YamlView } from './YamlView';
import { fetchPreview, useDefinitionPreview } from '../hooks/useDefinitionPreview';
import { useEditorOptions } from '../hooks/useEditorOptions';
import { useFormRender } from '../hooks/useFormRender';
import { useSaveDefinition } from '../hooks/useProjectPanelMutations';
import { validName } from '../utils/definitionName';
import { lineEndingOf, toLf, withLineEnding } from '../utils/lineEndings';
import {
  definitionTemplate,
  fieldsFrom,
  nameInYaml,
  templateFields,
  type DefinitionFields,
} from '../utils/definitionForm';
import { panelErrorMessage } from '../utils/errors';
import type { LintFinding } from '../types/projectPanel.types';

interface DefinitionEditorProps {
  projectId: string;
  /** The definition's name; null for a new one. */
  name: string | null;
  /** The text Save replaces (`DefinitionEditStart.content`); null for a new one. */
  initialContent: string | null;
  /** The panel's `web_url`, for a refusal's Open in GitLab link. */
  projectWebUrl: string | null;
  /** The project's definition names, enabled and proposed: a new one may not reuse them. */
  existingNames: string[];
  /** A save's MR; called only while this editor is still open. */
  onSaved: (mrUrl: string, name: string) => void;
  onClose: () => void;
  /** Why Save is unavailable (the project's setup is incomplete); null when it isn't. */
  saveBlocked?: string | null;
  /** Whether the text differs from what the editor opened with, or last saved. */
  onDirtyChange?: (dirty: boolean) => void;
  /** Opened by the viewer's own action: focus the heading. */
  focusOnShow?: boolean;
}

type View = 'yaml' | 'form';

const NO_NAME = 'give the definition a name: lowercase letters, digits and dashes, starting with a letter or digit';

const finding = (message: string): LintFinding => ({ path: '', severity: 'error', message });

export function DefinitionEditor(props: DefinitionEditorProps): ReactElement {
  const { projectId } = props;
  const client = useQueryClient();
  const [ending] = useState(() => lineEndingOf(props.initialContent));
  const [text, setText] = useState(() => toLf(props.initialContent ?? definitionTemplate(props.name ?? '')));
  const [opened] = useState(text);
  const [base, setBase] = useState<string | null>(props.initialContent);
  const open = useRef(true); // false once closed: a late save then leaves the page alone
  useEffect(() => { open.current = true; return () => { open.current = false; }; }, []);
  const [fixedName, setFixedName] = useState<string | null>(props.name);
  const [view, setView] = useState<View>(props.name === null ? 'form' : 'yaml');
  const [fields, setFields] = useState<DefinitionFields>(templateFields);
  // The text whose preview was no valid definition, and what it found.
  const [blocked, setBlocked] = useState<{ text: string; findings: LintFinding[] } | null>(null);
  const [switching, setSwitching] = useState(false);
  const [switchError, setSwitchError] = useState<string | null>(null);
  const render = useFormRender(projectId, setText);
  const editorOptions = useEditorOptions(projectId).data;
  const save = useSaveDefinition(projectId);

  const name = fixedName ?? (view === 'form' ? validName(fields.name) : nameInYaml(text));
  const preview = useDefinitionPreview(projectId, name, text);
  const isBlocked = blocked !== null && blocked.text === text;

  const local: LintFinding[] = [];
  if (fixedName === null && name === null) local.push(finding(NO_NAME));
  if (fixedName === null && name !== null && props.existingNames.includes(name)) {
    local.push(finding(`a definition named ${name} already exists; select it in the list to edit it`));
  }
  const formRefused = view === 'form' && render.error !== null;
  if (formRefused) local.push(finding(render.error ?? ''));
  const previewFindings = preview.result?.findings ?? (isBlocked ? blocked.findings : preview.shown?.findings);
  const findings = [...local, ...(formRefused ? [] : (previewFindings ?? []))];

  const inSync = view === 'yaml' || (!render.pending && render.error === null);
  const formDisabled = name === null || isBlocked || (view === 'yaml' && preview.result?.definition === null);
  const unchanged = base !== null && text === toLf(base);
  const dirty = text !== (base === null ? opened : toLf(base)) || render.pending;
  const { onDirtyChange } = props;
  useEffect(() => { onDirtyChange?.(dirty); }, [dirty, onDirtyChange]);
  const gateErrors = [...local, ...(preview.result?.findings ?? [])].some((f) => f.severity === 'error');
  const saveBlocked = props.saveBlocked ?? null;
  const canSave = name !== null && preview.result !== undefined && inSync && !gateErrors && !unchanged && !save.isPending && saveBlocked === null;

  const changeForm = (next: DefinitionFields): void => {
    const locked = fixedName === null ? next : { ...next, name: fixedName };
    setFields(locked);
    render.request(locked);
  };

  const toForm = async (): Promise<void> => {
    if (view === 'form' || name === null) return;
    const at = text;
    setSwitching(true);
    setSwitchError(null);
    try {
      const result = await fetchPreview(client, projectId, name, at);
      if (result.definition === null) {
        setBlocked({ text: at, findings: result.findings });
      } else {
        const parsed = fieldsFrom(result.definition);
        setFields(fixedName === null ? parsed : { ...parsed, name: fixedName });
        setView('form');
      }
    } catch (error) {
      setSwitchError(panelErrorMessage(error));
    } finally {
      setSwitching(false);
    }
  };

  const onSave = (): void => {
    if (name === null) return;
    const content = withLineEnding(text, ending);
    save.mutateAsync({ name, content, baseContent: base }).then(
      (result) => {
        setBase(content);
        setFixedName(name);
        if (open.current) props.onSaved(result.mr_url, name);
      },
      () => undefined, // shown from `save.error`
    );
  };

  return (
    <div className="def-editor">
      <DefinitionEditorHeader
        title={fixedName === null ? 'New run definition' : `Edit ${fixedName}`}
        view={view}
        formDisabled={view === 'yaml' && (formDisabled || switching)}
        yamlDisabled={!inSync}
        focusOnShow={props.focusOnShow === true}
        onForm={() => { void toForm(); }}
        onYaml={() => { setView('yaml'); }}
      />
      {view === 'yaml' && isBlocked && (
        <p className="def-editor__blocked" role="note">The form needs a valid definition; fix the YAML first.</p>
      )}
      {switchError !== null && <div className="error-message" role="alert">Couldn&apos;t open the form: {switchError}</div>}
      {view === 'form' ? (
        <DefinitionForm fields={fields} nameLocked={fixedName !== null} onChange={changeForm} options={editorOptions} />
      ) : (
        <YamlView value={text} readOnly={switching} onChange={(next) => { if (!switching) setText(next); }} />
      )}
      {/* Outside the live region: announced on every typing pause, it would talk over the editing. */}
      {preview.checking && name !== null && <p className="def-detail__hint">Checking…</p>}
      <div className="def-editor__checks" aria-live="polite">
        {render.pending && view === 'form' && <p className="def-detail__hint">Updating the YAML…</p>}
        {preview.error !== null && (
          <div className="error-message" role="alert">
            Couldn&apos;t check the definition: {panelErrorMessage(preview.error)}{' '}
            <button type="button" className="def-detail__link-button" onClick={preview.retry}>Check again</button>
          </div>
        )}
        <EditorFindings findings={findings} />
        {preview.result !== undefined && findings.length === 0 && <p className="def-ok">✓ valid</p>}
      </div>
      <div className="def-actions">
        <button type="button" className="save-button" disabled={!canSave} onClick={onSave}>
          {save.isPending ? 'Saving…' : 'Save'}
        </button>
        <button type="button" className="secondary-button" onClick={props.onClose}>Close</button>
        {unchanged && !save.isPending && <span className="def-detail__hint">No changes to save.</span>}
        {saveBlocked !== null && <span className="def-detail__hint">{saveBlocked}</span>}
      </div>
      <SaveOutcome result={save.data} error={save.error} projectWebUrl={props.projectWebUrl} />
    </div>
  );
}
